import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel, Heading, Highlight, IconButton, Image, Input, InputGroup, InputRightElement, Link, Modal, ModalBody, ModalContent, ModalOverlay, Text, Tooltip } from '@chakra-ui/react';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import { PiPaperPlaneRightFill } from 'react-icons/pi';
import { RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { trackSignIn } from '../../../helper/utils/eventTrackGA';
import { urlNavigate } from '../../../helper/utils/navigation';
import getStaticAsset from '../../../helper/utils/utilsStaticAsset';
import { doSuccess } from '../../../redux/responseHandling/responseHandling.actions';
import { doCheckEmail, doLogin } from '../../../redux/waiver/waiver.actions';
import useOnlineSales from '../hooks/useOnlineSales';

export default function ModalLogIn({ isOpen, onClose }) {
    const isVerifiedWaiver = (localStorage.getItem('isVerifiedWaiver') || 'false') === 'true';
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = React.useState(false);
    const [isNewCustomer, setIsNewCustomer] = React.useState(true);
    const [params, setParams] = useSearchParams();
    const handlePasswordClick = () => setShowPassword(!showPassword);

    const { loadingSignIn: loading = false } = useSelector((s) => s?.waiver);
    const { brand } = useOnlineSales();

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: '',
            password: '',
        },
        onSubmit: (values) => {
            const payload = {
                ...values,
                brand: String(brand['brandName'] || '')
                    .trim()
                    .toLowerCase(),
            };

            dispatch(
                doLogin(payload, null, (response) =>
                    dispatch(
                        doCheckEmail(values, navigate, (data) => {
                            const callbackUrl = (params.get('callback') || '').toString();

                            trackSignIn();

                            handleClose();
                            dispatch(doSuccess(`Welcome, ${data['fullname'] || ''}!`, '', 'Verify'));

                            setParams({
                                date: params.get('date') || '',
                                productId: params.get('productId') || '',
                                isShowDetail: (params.get('productId') || '') !== '',
                                type: params.get('type') || '',
                                callback: callbackUrl,
                            });

                            if (callbackUrl && callbackUrl != '') {
                                navigate(callbackUrl);
                            }
                        }),
                    ),
                ),
            );
        },
    });

    const handleClose = () => {
        onClose();
        setShowPassword(true);
        formik.handleReset();
    };

    useEffect(() => {
        if (isOpen) {
            setIsNewCustomer(true);
        }
    }, [isOpen]);

    return (
        <Modal isOpen={isVerifiedWaiver ? false : isOpen} onClose={handleClose} size='md' scrollBehavior='outside' isCentered closeOnEsc={true} closeOnOverlayClick={true}>
            <ModalOverlay />
            <ModalContent rounded='2xl'>
                <ModalBody py={12}>
                    <Box w='full' mb={3} display='flex' alignItems='center' justifyContent='center' flexDirection={isNewCustomer ? 'column-reverse' : 'column'} gap={5}>
                        <Flex w='full' alignItems='center' justifyContent='center' gap={3}>
                            <Image src={getStaticAsset('[PPI] Logo.png')} w='80px' h='90%' fit='contain' mb={3} />
                            <Image src={getStaticAsset('[SPI] Logo_Purple.png')} w='80px' h='90%' fit='contain' mb={3} />
                            <Image src={getStaticAsset('[TSI] Logo.png')} w='80px' h='90%' fit='contain' mb={3} />
                        </Flex>
                        <Heading fontSize={{ base: '2xl', md: '3xl' }} textAlign='center'>
                            {isNewCustomer ? 'First time in Our Park?' : 'Log In to Continue'}
                        </Heading>
                    </Box>
                    <Text fontSize='md' color='black' lineHeight='1.3' mb={2} textAlign='center'>
                        {isNewCustomer ? 'Before proceeding with the transaction, please log in to your account.' : 'Use your registered email to proceed'}
                    </Text>
                    {!isNewCustomer && (
                        <Text fontSize='md' color='black' lineHeight='1.3' mb={5} textAlign='center' fontStyle='italic' fontWeight={600}>
                            Login verifies your waiver and is not the same as Membership.
                        </Text>
                    )}
                    {isNewCustomer ? (
                        <Box w='full' h='auto' mt={7}>
                            <Flex w='full' flexDirection='column' gap={3}>
                                <Button
                                    size='lg'
                                    w='full'
                                    fontSize='md'
                                    py={7}
                                    variant='solid'
                                    colorScheme='primary'
                                    onClick={() => {
                                        setIsNewCustomer(true);
                                        navigate(urlNavigate('waiver', 'user'));
                                    }}>
                                    YES
                                </Button>
                                <Button size='lg' w='full' fontSize='md' py={7} onClick={() => setIsNewCustomer(false)}>
                                    NO
                                </Button>
                            </Flex>
                        </Box>
                    ) : (
                        <form onSubmit={formik.handleSubmit}>
                            <Flex w='full' h='auto' flexDirection='column' gap={2}>
                                <FormControl isInvalid={!!formik?.errors.email && formik?.touched.email}>
                                    <FormLabel fontSize='sm' color='black' lineHeight='1.3'>
                                        Email
                                    </FormLabel>
                                    <Input size='lg' id='email' name='email' variant='filled' type='email' py={7} onChange={formik.handleChange} value={formik?.values?.email ?? ''} placeholder='Enter email address' fontSize='14px' />
                                    <FormErrorMessage>{formik?.errors.email}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!formik?.errors.password && formik?.touched.password}>
                                    <FormLabel fontSize='sm' color='black' lineHeight='1.3'>
                                        Password
                                    </FormLabel>
                                    <InputGroup size='lg'>
                                        <Input id='password' name='password' variant='filled' type={showPassword ? 'password' : 'text'} py={7} onChange={formik.handleChange} value={formik?.values?.password ?? ''} placeholder='Enter password' fontSize='14px' />
                                        <InputRightElement h='full' pr={3} transition='all 0.25s ease-out'>
                                            <Tooltip label={showPassword ? 'Show' : 'Hide'}>
                                                <IconButton icon={showPassword ? <RiEyeLine /> : <RiEyeOffLine />} bg='transparent' _hover={{ bg: 'transparent' }} color={showPassword ? 'gray.400' : 'gray.600'} onClick={handlePasswordClick} />
                                            </Tooltip>
                                        </InputRightElement>
                                    </InputGroup>
                                    <FormErrorMessage>{formik?.errors.password}</FormErrorMessage>
                                </FormControl>
                                <Box w='full'>
                                    <Link as={RouterLink} to={urlNavigate('forgot-password')} fontSize='sm' _hover={{ textDecoration: 'none' }}>
                                        <Highlight query={['Forget Password?']} styles={{ color: 'primary.500', fontWeight: 600 }}>
                                            Forget Password?
                                        </Highlight>
                                    </Link>
                                </Box>
                                <Box w='full' mt={4}>
                                    <Button variant='solid' w='full' isDisabled={formik.values.email.length === 0 || formik.values.password.length === 0} colorScheme='primary' rightIcon={<PiPaperPlaneRightFill />} isLoading={loading} size='lg' fontSize='15px' type='submit'>
                                        Log Me In!
                                    </Button>
                                </Box>
                            </Flex>
                        </form>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
